import { hora12, nombreServicios, serviciosDe, type CitaVista } from '@barbones/shared';
import { useEffect, useRef, useState, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { CLASES_TARJETA } from './EstadoCita';

/** Alto de cada renglón y del encabezado de un grupo, en px. */
export const ALTO_RENGLON = 26;
export const ALTO_ENCABEZADO = 22;

interface Props {
  /** Citas del grupo, ordenadas por inicio (al menos dos). */
  citas: CitaVista[];
  top: number;
  alto: number;
  onCita: (cita: CitaVista) => void;
  /** Atributos de arrastre de cada renglón (los mismos que una tarjeta suelta). */
  propsArrastre: (cita: CitaVista) => ButtonHTMLAttributes<HTMLButtonElement>;
}

const etiqueta = (c: CitaVista) => `${hora12(new Date(c.inicio))} · ${c.cliente.nombre} · ${nombreServicios(serviciosDe(c))}`;

function Renglon({ cita, onCita, propsArrastre }: { cita: CitaVista } & Pick<Props, 'onCita' | 'propsArrastre'>) {
  return (
    <button
      type="button"
      {...propsArrastre(cita)}
      onClick={() => onCita(cita)}
      aria-label={etiqueta(cita)}
      className={cn(
        'flex w-full shrink-0 items-center truncate rounded border border-border/60 border-l-[3px] px-1.5 text-left text-[11.5px] leading-[22px] hover:-translate-y-px',
        CLASES_TARJETA[cita.estado],
        propsArrastre(cita).className,
      )}
      style={{ height: ALTO_RENGLON - 4 }}
    >
      <span className="truncate">{etiqueta(cita)}</span>
    </button>
  );
}

/**
 * Citas sin barbero que se enciman: una caja del primer inicio al último fin con un renglón por cita.
 * Si no caben todas, «+N más» despliega la lista completa encima de las filas siguientes.
 */
export function GrupoSinAsignar({ citas, top, alto, onCita, propsArrastre }: Props) {
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLDivElement>(null);
  const visibles = Math.max(1, Math.floor((alto - ALTO_ENCABEZADO) / ALTO_RENGLON));
  const ocultas = citas.length - visibles;
  const inicio = new Date(citas[0]!.inicio);
  const fin = new Date(Math.max(...citas.map((c) => new Date(c.fin).getTime())));

  useEffect(() => {
    if (!abierto) return;
    const alTeclear = (e: KeyboardEvent) => e.key === 'Escape' && setAbierto(false);
    const alTocar = (e: MouseEvent) => caja.current && !caja.current.contains(e.target as Node) && setAbierto(false);
    document.addEventListener('keydown', alTeclear);
    document.addEventListener('mousedown', alTocar);
    return () => {
      document.removeEventListener('keydown', alTeclear);
      document.removeEventListener('mousedown', alTocar);
    };
  }, [abierto]);

  return (
    <div
      ref={caja}
      className={cn('absolute inset-x-1 z-[5] flex flex-col gap-0.5 rounded-md border border-border bg-surface-alt px-1 pb-1 shadow-hoja', abierto && 'z-20')}
      style={{ top: top + 1, height: alto }}
    >
      <div className="flex items-center justify-between gap-1 px-0.5" style={{ height: ALTO_ENCABEZADO - 2 }}>
        <span className="cifras truncate text-[11px] font-semibold text-foreground-muted">
          {citas.length} citas · {hora12(inicio)}–{hora12(fin)}
        </span>
        {ocultas > 0 && (
          <button type="button" onClick={() => setAbierto((v) => !v)} aria-expanded={abierto} className="shrink-0 rounded px-1 text-[11px] font-semibold text-accent hover:bg-accent-soft">
            +{ocultas} más
          </button>
        )}
      </div>
      {citas.slice(0, visibles).map((c) => (
        <Renglon key={c.id} cita={c} onCita={onCita} propsArrastre={propsArrastre} />
      ))}
      {abierto && (
        <div role="dialog" aria-label={`${citas.length} citas sin asignar`} className="absolute inset-x-0 top-full z-20 mt-1 flex max-h-72 flex-col gap-0.5 overflow-y-auto rounded-md border border-border bg-surface p-1 shadow-flotante">
          {citas.map((c) => (
            <Renglon key={c.id} cita={c} onCita={onCita} propsArrastre={propsArrastre} />
          ))}
        </div>
      )}
    </div>
  );
}
